import React from 'react'
import {Link, useLoaderData, useRouteLoaderData} from '@remix-run/react'
import {textToSlug} from '@roundforest/text-transforms-commons'
import clsx from 'clsx'
import {useReportWidgetClick, useReportWidgetSeen} from '~/hooks/analytic-hooks'
import {getTranslation} from '~/localization/translation'
import type {CategoriesDataProps, RootLoaderResponse} from '~/types'
import {getDomainConfig} from '~/domains'

const Header = () => {
  const {language, domain, locale} = useRouteLoaderData('root') as RootLoaderResponse
  const {isDomainNonCategorical} = useLoaderData() as CategoriesDataProps
  const {styles: stylesBydomain} = getDomainConfig(domain, locale)

  const translations = getTranslation(language)
  const topRatedLabel = translations.Home?.topRated
  const topRatedData = translations.Home?.topRatedData ?? []
  const sendWidgetClickEvent = useReportWidgetClick()
  const [widgetRef] = useReportWidgetSeen({
    widget_name: 'home-page-header',
    widget_place: 'top',
  })

  return (
    <header
      className={clsx(
        'flex h-56 justify-center tablet:h-fit',
        stylesBydomain.HOMEPAGE_HEADER_BG,
        stylesBydomain.HOMEPAGE_HEADER_TEXT,
        'homepage_header_bg homepage_header_text',
      )}
      ref={widgetRef}
    >
      <div
        id="wrapper"
        className={clsx(
          stylesBydomain.UTILS_MAIN_CONTAINER,
          isDomainNonCategorical ? 'pt-5' : 'my-auto tablet:pb-6 tablet:pt-2',
          'utils_main_container',
        )}
        data-testid="home-page-header"
      >
        <div className="mt-3 flex flex-col justify-center text-center">
          <span
            className={clsx(
              'relative mx-auto w-fit text-sm font-light before:-left-4 before:top-0.5 before:text-xs before:content-["★"] mobile:text-[13px]',
              stylesBydomain.HOMEPAGE_HEADER_TITLE_STAR,
            )}
          >
            {translations.Home.Header.peopleSavedTitle}
          </span>
          <h1
            className="mt-3 text-center text-[40px] font-bold tablet:text-[32px] tablet:leading-10 [&>span]:after:content-['.']"
            data-testid="home-page-title"
          >
            <span className={clsx('homepage_header_title_dot', stylesBydomain.HOMEPAGE_HEADER_TITLE_DOT)}>
              {translations.Home.Header.topRatedPicks}
            </span>
            <br className="desktop:hidden" />
            <span className={clsx('homepage_header_title_dot', stylesBydomain.HOMEPAGE_HEADER_TITLE_DOT)}>
              {translations.Home.Header.bestPrices}
            </span>
          </h1>
        </div>
        <h2
          data-testid="home-page-subtitle"
          className="flex justify-center text-center text-base font-light tracking-wide"
        >
          {translations.Home.Header.expertReviewsDiscounts}
        </h2>
        {isDomainNonCategorical && (
          <div className="no-scrollbar w-full overflow-x-auto overflow-ellipsis whitespace-nowrap desktop:flex desktop:justify-center">
            <div data-testid="top-rated-links" className="mt-6 items-center justify-center pb-10 text-sm tablet:pb-7">
              <span className="tablet:hidden">{topRatedLabel ? `${topRatedLabel}:` : ''}</span>
              {topRatedData.map((category, index) => {
                return (
                  <Link
                    reloadDocument
                    className={clsx(
                      'mx-2 rounded-full border border-solid px-4 py-1 text-xs',
                      stylesBydomain.HOMEPAGE_TOPRATED_BUTTON,
                      'homepage_toprated_button',
                    )}
                    key={`${category.title}-${index}`}
                    to={`/${textToSlug(category.title)}`}
                    data-testid={`link-to-${textToSlug(category.title)}`}
                    onClick={() =>
                      sendWidgetClickEvent({
                        trigger_type: `${index + 1}-${category.title}`,
                        widget_name: 'home-page-top-rated',
                        widget_place: 'top',
                      })
                    }
                  >
                    {category.title}
                  </Link>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </header>
  )
}

export default Header
